import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, Clock, User, MapPin, Info, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Palette, BookOpen } from 'lucide-react';
import SEOHead, { EventSchema } from '../components/SEOHead';
import { sanitizeHtml, stripHtmlToText } from '../utils/sanitize';

const API_URL = "";

// Image Slider Component
const ImageSlider = ({ images, title, fallbackImage }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  
  // Ensure we have at least one image and handle API URLs
  const imageList = (images && images.length > 0 ? images : []).map(img => 
    img.startsWith('/api') ? `${API_URL}${img}` : img
  );
  
  // Filter out images that have failed to load
  const validImages = imageList.filter((_, index) => !imageErrors[index]);
  
  // If all images failed, try fallback
  if (validImages.length === 0) {
    if (fallbackImage) {
      return (
        <div className="relative w-full h-80 overflow-hidden rounded-lg">
          <img 
            src={fallbackImage.startsWith('/api') ? `${API_URL}${fallbackImage}` : fallbackImage} 
            alt={title}
            className="w-full h-full object-cover object-top"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      );
    }
    return null;
  }
  
  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
    // Move to next valid image if current fails
    if (currentIndex === index) {
      const nextValid = imageList.findIndex((_, i) => i > index && !imageErrors[i]);
      if (nextValid !== -1) {
        setCurrentIndex(nextValid);
      }
    }
  };
  
  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  };
  
  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  };
  
  return (
    <div className="relative w-full h-80 overflow-hidden rounded-lg group bg-stone-100">
      <img 
        src={imageList[currentIndex]} 
        alt={`${title} - Image ${currentIndex + 1}`}
        className="w-full h-full object-cover object-top transition-all duration-300"
        onError={() => handleImageError(currentIndex)}
      />
      
      {/* Navigation arrows - only show if more than 1 image */}
      {imageList.length > 1 && (
        <>
          <button 
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Next image"
          >
            <ChevronRight size={20} />
          </button>
          
          {/* Dots indicator */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {imageList.map((_, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-white w-4' : 'bg-white/50'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Expandable Description Component
const ExpandableDetails = ({ workshop }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const hasCurriculum = workshop.curriculum && workshop.curriculum.length > 0;
  const hasMaterialsDetailed = workshop.materials_detailed && workshop.materials_detailed.length > 0;
  const hasExpandableContent = hasCurriculum || hasMaterialsDetailed;
  
  if (!hasExpandableContent) return null;
  
  return (
    <div className="mt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-cafa-red hover:text-cafa-red-900 font-medium transition-colors"
        data-testid={`expand-details-${workshop.id}`}
      >
        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        {isExpanded ? 'Hide Details' : 'View Workshop Details'}
      </button>
      
      {isExpanded && (
        <div className="mt-4 space-y-6 bg-stone-50 rounded-lg p-5 border border-stone-200 animate-in slide-in-from-top-2">
          {/* What You'll Learn / Curriculum */}
          {hasCurriculum && (
            <div>
              <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <BookOpen size={18} className="text-cafa-red" />
                What You&apos;ll Learn
              </h4>
              <ul className="space-y-2">
                {workshop.curriculum.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-stone-600">
                    <span className="text-cafa-red mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* Materials Needed */}
          {hasMaterialsDetailed && (
            <div>
              <h4 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
                <Palette size={18} className="text-cafa-red" />
                Materials Needed
              </h4>
              <ul className="space-y-2">
                {workshop.materials_detailed.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-stone-600">
                    <span className="text-cafa-red mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const Workshops = () => {
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorkshops = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/workshops`);
        if (res.ok) {
          const data = await res.json();
          // Transform data - keep instructorImage separate, use images for slider
          const transformedData = data.map(w => ({
            ...w,
            // Images array is for the main slider
            images: w.images && w.images.length > 0 
              ? w.images 
              : [],
            // Keep instructorImage as separate field for faculty photo
            instructorImage: w.instructorImage || ''
          }));
          setWorkshops(transformedData);
        }
      } catch (err) {
        console.error('Error fetching workshops:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkshops();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-stone-600">Loading workshops...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="workshops-page">
      <SEOHead
        title="Workshops — Master Artist Intensives"
        description="Intensive art workshops with nationally recognized artists at The Coppini Academy of Fine Art in San Antonio. Multi-day painting, drawing, and technique masterclasses."
        canonicalPath="/workshops"
      />
      {workshops.map((workshop) => (
        <EventSchema
          key={`event-schema-${workshop.id}`}
          name={workshop.title}
          description={stripHtmlToText(workshop.description).slice(0, 500) || workshop.title}
          startDate={workshop.date}
          endDate={workshop.endDate}
          price={workshop.nonMemberPrice ?? workshop.price}
          performer={workshop.instructor}
        />
      ))}
      {/* Hero - Dark Burgundy Bar */}
      <section className="bg-cafa-red py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-4">Workshops</h1>
          <p className="text-xl text-white/90">
            Intensive workshops with nationally recognized artists and master instructors
          </p>
        </div>
      </section>

      {/* Workshops List */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {workshops.length === 0 ? (
            <div className="text-center text-stone-600 py-12">
              <p>No workshops are currently scheduled. Please check back later!</p>
            </div>
          ) : (
            <div className="space-y-8">
              {workshops.map((workshop) => (
                <Card key={workshop.id} className="border-stone-200 hover:shadow-lg transition-shadow" data-testid={`workshop-card-${workshop.id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Image Slider or Instructor Image */}
                      <div className="md:w-80 flex-shrink-0">
                        {workshop.images && workshop.images.length > 0 ? (
                          <ImageSlider 
                            images={workshop.images} 
                            title={workshop.title} 
                            fallbackImage={workshop.instructorImage}
                          />
                        ) : workshop.instructorImage ? (
                          <div className="aspect-[4/3] rounded-lg overflow-hidden bg-stone-100">
                            <img 
                              src={workshop.instructorImage.startsWith('/api') ? `${API_URL}${workshop.instructorImage}` : workshop.instructorImage} 
                              alt={workshop.instructor}
                              className="w-full h-full object-cover object-top"
                              onError={(e) => { 
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240"><rect fill="%23f5f5f4" width="320" height="240"/><text fill="%23a8a29e" x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="system-ui" font-size="14">Image unavailable</text></svg>';
                              }}
                            />
                          </div>
                        ) : (
                          <div className="aspect-[4/3] rounded-lg bg-stone-100 flex items-center justify-center">
                            <span className="text-stone-400 text-sm">No image</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Workshop Details */}
                      <div className="flex-1 flex flex-col">
                        <div className="mb-4">
                          <h3 className="text-2xl font-semibold text-stone-900 mb-2">{workshop.title}</h3>
                          <div className="flex items-center gap-3">
                            {/* Instructor photo (small, shown when there are workshop images) */}
                            {workshop.instructorImage && workshop.images && workshop.images.length > 0 && (
                              <img 
                                src={workshop.instructorImage.startsWith('/api') ? `${API_URL}${workshop.instructorImage}` : workshop.instructorImage} 
                                alt={workshop.instructor}
                                className="w-12 h-12 rounded-full object-cover border-2 border-cafa-gold"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <p className="text-lg text-cafa-red font-medium">{workshop.instructor}</p>
                          </div>
                        </div>
                        
                        <div 
                          className="text-stone-600 mb-4 prose prose-stone prose-sm max-w-none [&_a]:text-cafa-blue [&_a]:underline"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(workshop.description) }}
                        />
                        
                        <div className="space-y-3 mb-4">
                          <div className="flex items-start gap-2 text-sm">
                            <CalendarIcon size={16} className="text-cafa-red mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-stone-700">Date:</span>{' '}
                              <span className="text-stone-600">{workshop.schedule}</span>
                            </div>
                          </div>
                          
                          {workshop.time && (
                            <div className="flex items-start gap-2 text-sm">
                              <Clock size={16} className="text-cafa-red mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-semibold text-stone-700">Time:</span>{' '}
                                <span className="text-stone-600">{workshop.time}</span>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin size={16} className="text-cafa-red mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-semibold text-stone-700">Location:</span>{' '}
                              <span className="text-stone-600">{workshop.location}</span>
                            </div>
                          </div>
                          
                          {workshop.level && (
                            <div className="flex items-start gap-2 text-sm">
                              <User size={16} className="text-cafa-red mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-semibold text-stone-700">Level:</span>{' '}
                                <span className="text-stone-600">{workshop.level}</span>
                              </div>
                            </div>
                          )}
                          
                          {workshop.materials && (
                            <div className="flex items-start gap-2 text-sm">
                              <Info size={16} className="text-cafa-red mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-semibold text-stone-700">Materials:</span>{' '}
                                <span className="text-stone-600">{workshop.materials}</span>
                              </div>
                            </div>
                          )}
                          
                          {workshop.contact && (
                            <div className="flex items-start gap-2 text-sm">
                              <Info size={16} className="text-cafa-red mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="font-semibold text-stone-700">Contact:</span>{' '}
                                <span className="text-stone-600">{workshop.contact}</span>
                              </div>
                            </div>
                          )}
                          
                          {workshop.note && (
                            <div className="bg-cafa-red-50 border border-cafa-blue-200 rounded p-3 text-sm">
                              <p className="text-cafa-red-900">{workshop.note}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Expandable Details Section */}
                        <ExpandableDetails workshop={workshop} />
                        
                        <div className="flex items-center justify-between pt-4 border-t border-stone-200 mt-auto">
                          <div>
                            {workshop.price !== null ? (
                              <div>
                                <div className="text-2xl font-bold text-cafa-red">
                                  ${workshop.memberPrice}
                                </div>
                                {workshop.memberPrice !== workshop.nonMemberPrice && (
                                  <div className="text-sm text-stone-500">
                                    Non-members: ${workshop.nonMemberPrice}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-lg font-semibold text-stone-600">
                                Contact for pricing
                              </div>
                            )}
                          </div>
                          <Button 
                            asChild
                            className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                            data-testid={`register-btn-${workshop.id}`}
                          >
                            <Link to={`/register?workshop=${workshop.id}`}>
                              Register
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Additional Info Section */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-6 text-center">About Our Workshops</h2>
          <div className="space-y-4 text-stone-700 leading-relaxed">
            <p>
              The Coppini Academy hosts workshops with nationally recognized artists throughout the year. 
              These intensive sessions provide focused instruction in various techniques and media.
            </p>
            <p>
              <strong>Member Benefits:</strong> Academy members receive discounted rates on most workshops. 
              Annual membership is $60 and provides access to discounts, open studio sessions, and more.
            </p>
            <p>
              <strong>Registration:</strong> Workshop dates and details are posted on our calendar. 
              Some workshops require advance registration and may have limited spots available.
            </p>
            <p>
              For more information or to register, contact us at <strong>(210) 530-8445</strong> or{' '}
              <strong>info@thecoppini.org</strong>
            </p>
          </div>
          
          {/* Cancellation Policy */}
          <div className="mt-10 p-6 bg-white rounded-lg border border-stone-200">
            <h3 className="text-xl font-semibold text-stone-900 mb-4">Cancellation Policy</h3>
            <ul className="space-y-3 text-stone-700">
              <li className="flex items-start gap-2">
                <span className="text-cafa-red font-bold">•</span>
                <span><strong>45+ days before workshop:</strong> Full refund</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cafa-red font-bold">•</span>
                <span><strong>30–44 days before workshop:</strong> 50% refund</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cafa-red font-bold">•</span>
                <span><strong>Less than 30 days before workshop:</strong> No refund</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cafa-red font-bold">•</span>
                <span><strong>Academy cancellation:</strong> If the Academy cancels a workshop, a full refund will be issued</span>
              </li>
            </ul>
            <p className="mt-4 text-sm text-stone-500">
              To request a cancellation, please contact us at <strong>info@thecoppini.org</strong> or call <strong>(210) 530-8445</strong>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
